import React, { FC } from 'react'
import { Switch, CircularProgress, Box, Stack, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  useReleaseChannel,
  useSettingQuery,
  useUpdateSettingMutation,
} from '@/features/auth'
import { Setting } from '@/features/auth/types/setting'
import {
  isV3SoapModel,
  resolveSoapModelLatest,
  selectableSoapModelMajors,
  soapModelMajorOf,
  type SoapModelMajor,
} from '@/features/auth/utils/soapModelVersion'
import { settingDescriptions } from '@/apps/latest/constants/settingDescriptions'
import {
  NotionButton,
  NotionSelect,
  PageScaffold,
  SettingRow,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'

export const SoapSettingsPage: FC = () => {
  const navigate = useNavigate()
  const { data: customSetting } = useSettingQuery()
  const { isBeta } = useReleaseChannel()
  const updateSettingMutation = useUpdateSettingMutation()

  const updateSetting = (key: keyof Setting, value: Setting[keyof Setting]) => {
    updateSettingMutation.mutate({ key, value })
  }

  if (!customSetting) {
    return (
      <PageScaffold title="SOAP生成設定">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageScaffold>
    )
  }

  const legacy = settingDescriptions.legacyCompat.items
  const speech = settingDescriptions.speechRecognition.items
  const output = settingDescriptions.soapOutput.items
  const rules = settingDescriptions.rules.items

  // ユーザーには major (v1 / v2 / v3) だけ見せ、選択時は当該 major の最新 patch を自動設定する.
  // v3 は GA。配信されている系統をそのまま出す (selectableSoapModelMajors 参照)
  const modelLatest = resolveSoapModelLatest(
    customSetting.soap_prompt_versions_available
  )
  const soapModelOptions = selectableSoapModelMajors(
    customSetting.soap_prompt_versions_available
  ).map((m) => ({ value: m, label: m }))

  // v3 = SoapRule / 記載傾向の蒸留の新世界。テンプレート・S幅広は存在しない
  const isV3 = isV3SoapModel(customSetting.soap_prompt_version)

  // しっかりモードが生成に効くか。server が model 設定表から導出して配る値をそのまま使う
  // (front で version から推測しない — 版と世代が食い違う状態があるため)。
  const quickModeEffective = customSetting.is_quick_mode_effective ?? true

  return (
    <PageScaffold title="SOAP生成設定">
      <Stack gap={4}>
        <Box>
          <SubsectionHeader heading={settingDescriptions.legacyCompat.title} />
          <SettingRow
            label={legacy.transcription_mode.title}
            description={legacy.transcription_mode.description}
            control={
              // 転写経路の選択 (log/207)。オン = batch (しっかり文字起こし) /
              // オフ = streaming (リアルタイム)。engine 側の batch 経路開通と同時に解禁。
              // 反映は次の録音から (進行中の録音の経路は変わらない)。
              <Switch
                color="primary"
                checked={customSetting.transcription_mode === 'batch'}
                onChange={() =>
                  updateSetting(
                    'transcription_mode',
                    customSetting.transcription_mode === 'batch'
                      ? 'streaming'
                      : 'batch'
                  )
                }
              />
            }
          />
          <SettingRow
            label={legacy.soap_prompt_version.title}
            description={legacy.soap_prompt_version.description}
            control={
              <NotionSelect<string>
                value={soapModelMajorOf(customSetting.soap_prompt_version)}
                onChange={(major) => {
                  const target = modelLatest[major as SoapModelMajor]
                  if (target) updateSetting('soap_prompt_version', target)
                }}
                minWidth={140}
                options={
                  soapModelOptions.length
                    ? soapModelOptions
                    : [{ value: '', label: '読込中', disabled: true }]
                }
              />
            }
          />
          <SettingRow
            label={legacy.use_legacy_ui.title}
            description={legacy.use_legacy_ui.description}
            control={
              <Switch
                color="primary"
                checked={customSetting.use_legacy_ui ?? false}
                onChange={() =>
                  updateSetting('use_legacy_ui', !customSetting.use_legacy_ui)
                }
              />
            }
          />
        </Box>

        <Box>
          <SubsectionHeader
            heading={settingDescriptions.speechRecognition.title}
          />
          <SettingRow
            label={speech.is_quick_mode.title}
            description={speech.is_quick_mode.description}
            control={
              // 効かない世代では常にしっかり側で生成されるため、オン固定の変更不可で見せる。
              // disabled な Switch はホバーを拾わないため span でラップして Tooltip を出す。
              quickModeEffective ? (
                <Switch
                  color="primary"
                  checked={!(customSetting.is_quick_mode ?? false)}
                  onChange={() =>
                    updateSetting(
                      'is_quick_mode',
                      !(customSetting.is_quick_mode ?? false)
                    )
                  }
                />
              ) : (
                <Tooltip title="現在のSOAPモデルでは常にオンです">
                  <span>
                    <Switch color="primary" checked disabled />
                  </span>
                </Tooltip>
              )
            }
          />
          <SettingRow
            label={speech.is_noise_cancelling.title}
            description={speech.is_noise_cancelling.description}
            control={
              <Switch
                color="primary"
                checked={customSetting.is_noise_cancelling ?? false}
                onChange={() =>
                  updateSetting(
                    'is_noise_cancelling',
                    !customSetting.is_noise_cancelling
                  )
                }
              />
            }
          />
          <SettingRow
            label={speech.capture_noise_suppression.title}
            description={speech.capture_noise_suppression.description}
            control={
              // null (未設定) は録音側の既定値 = オンとして表示する。
              <Switch
                color="primary"
                checked={customSetting.capture_noise_suppression !== false}
                onChange={() =>
                  updateSetting(
                    'capture_noise_suppression',
                    customSetting.capture_noise_suppression === false
                  )
                }
              />
            }
          />
        </Box>

        <Box>
          <SubsectionHeader heading={settingDescriptions.soapOutput.title} />
          {/* v3 では S幅広 (v1のみ有効) と SOAPテンプレート (v3で廃止) を出さない */}
          {!isV3 && (
            <SettingRow
              label={output.is_enrich_subjective.title}
              description={output.is_enrich_subjective.description}
              control={
                <Switch
                  color="primary"
                  checked={customSetting.is_enrich_subjective || false}
                  onChange={() =>
                    updateSetting(
                      'is_enrich_subjective',
                      !customSetting.is_enrich_subjective
                    )
                  }
                />
              }
            />
          )}
          <SettingRow
            label={output.should_enhance_soap.title}
            description={output.should_enhance_soap.description}
            control={
              <Switch
                color="primary"
                checked={customSetting.should_enhance_soap || false}
                onChange={() =>
                  updateSetting(
                    'should_enhance_soap',
                    !customSetting.should_enhance_soap
                  )
                }
              />
            }
          />
          <SettingRow
            label={output.is_short_ep.title}
            description={output.is_short_ep.description}
            control={
              <Switch
                color="primary"
                checked={customSetting.is_short_ep ?? false}
                onChange={() =>
                  updateSetting('is_short_ep', !customSetting.is_short_ep)
                }
              />
            }
          />
          {!isV3 && (
            <SettingRow
              label={output.use_soap_template.title}
              description={output.use_soap_template.description}
              control={
                <Switch
                  color="primary"
                  checked={customSetting.use_soap_template ?? false}
                  onChange={() =>
                    updateSetting(
                      'use_soap_template',
                      !customSetting.use_soap_template
                    )
                  }
                />
              }
            />
          )}
        </Box>

        <Box>
          <SubsectionHeader heading={settingDescriptions.rules.title} />
          {isV3 ? (
            <>
              <SettingRow
                label={rules.soap_rules.title}
                description={rules.soap_rules.description}
                control={
                  <NotionButton
                    variant="secondary"
                    onClick={() => navigate('/portal/soap-rules')}
                  >
                    開く
                  </NotionButton>
                }
              />
              {isBeta && (
                <SettingRow
                  label={rules.tracing_reports.title}
                  description={rules.tracing_reports.description}
                  control={
                    <NotionButton
                      variant="secondary"
                      onClick={() => navigate('/portal/tracing-reports')}
                    >
                      開く
                    </NotionButton>
                  }
                />
              )}
            </>
          ) : (
            <>
              <SettingRow
                label={rules.soap_templates.title}
                description={rules.soap_templates.description}
                control={
                  <NotionButton
                    variant="secondary"
                    onClick={() => navigate('/portal/soaps')}
                  >
                    開く
                  </NotionButton>
                }
              />
              <SettingRow
                label={rules.format_insert.title}
                description={rules.format_insert.description}
                control={
                  <NotionButton
                    variant="secondary"
                    onClick={() => navigate('/portal/format-insert-settings')}
                  >
                    開く
                  </NotionButton>
                }
              />
              <SettingRow
                label={rules.text_replacements.title}
                description={rules.text_replacements.description}
                control={
                  <NotionButton
                    variant="secondary"
                    onClick={() => navigate('/portal/text-replacements')}
                  >
                    開く
                  </NotionButton>
                }
              />
            </>
          )}
        </Box>
      </Stack>
    </PageScaffold>
  )
}

export default SoapSettingsPage
